import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Save, 
  Download,
  X,
  Layout,
  Code,
  Palette,
  Sparkles
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { markFreeCourseDownloadUsed } from "@/lib/freeTier";
import VisualSlideCanvas from "./VisualSlideCanvas";
import ThemeDesignerDialog from "./ThemeDesignerDialog";

interface Slide {
  id: string;
  index: number;
  title: string;
  bullets: string[];
  notes: string;
  imageUrl?: string | null;
}

interface SlideTheme {
  backgroundColor: string;
  titleColor: string;
  textColor: string;
  accentColor: string;
  fontFamily: string;
  titleFontSize: number;
  textFontSize: number;
  bulletStyle: string;
  backgroundGradient?: string | null;
  description?: string;
}

interface SlideDeck {
  title: string;
  slides: Slide[];
  theme?: SlideTheme;
}

interface SlideEditorProps {
  deckId: string;
  initialDeck: SlideDeck;
  onClose: () => void;
  onSave: () => void;
}

const SlideEditor = ({ deckId, initialDeck, onClose, onSave }: SlideEditorProps) => {
  const [deck, setDeck] = useState<SlideDeck>(initialDeck);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const { toast } = useToast();

  const currentSlide = deck.slides[currentSlideIndex];

  const updateSlide = (updates: Partial<Slide>) => {
    setDeck(prev => ({
      ...prev,
      slides: prev.slides.map((slide, idx) => 
        idx === currentSlideIndex ? { ...slide, ...updates } : slide
      )
    }));
  };

  const addBullet = () => {
    updateSlide({ 
      bullets: [...currentSlide.bullets, ''] 
    });
  };

  const updateBullet = (bulletIndex: number, value: string) => {
    const newBullets = [...currentSlide.bullets];
    newBullets[bulletIndex] = value;
    updateSlide({ bullets: newBullets });
  };

  const removeBullet = (bulletIndex: number) => {
    updateSlide({ 
      bullets: currentSlide.bullets.filter((_, idx) => idx !== bulletIndex) 
    });
  };

  const addSlide = () => {
    const newSlideId = `slide-${deck.slides.length + 1}`;
    const newSlide: Slide = {
      id: newSlideId,
      index: deck.slides.length + 1,
      title: 'New Slide',
      bullets: [''],
      notes: '',
      imageUrl: null
    };
    
    setDeck(prev => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));
    
    setCurrentSlideIndex(deck.slides.length);
  };

  const deleteSlide = () => {
    if (deck.slides.length === 1) {
      toast({
        variant: "destructive",
        title: "Cannot delete",
        description: "You must have at least one slide"
      });
      return;
    }

    setDeck(prev => ({
      ...prev,
      slides: prev.slides
        .filter((_, idx) => idx !== currentSlideIndex)
        .map((slide, idx) => ({ ...slide, index: idx + 1, id: `slide-${idx + 1}` }))
    }));
    
    if (currentSlideIndex >= deck.slides.length - 1) {
      setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1));
    }
  };

  const moveSlide = (direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? currentSlideIndex - 1 : currentSlideIndex + 1;
    
    if (newIndex < 0 || newIndex >= deck.slides.length) return;

    const newSlides = [...deck.slides];
    [newSlides[currentSlideIndex], newSlides[newIndex]] = [newSlides[newIndex], newSlides[currentSlideIndex]];
    
    // Update indices
    const reindexed = newSlides.map((slide, idx) => ({ ...slide, index: idx + 1 }));
    
    setDeck(prev => ({ ...prev, slides: reindexed }));
    setCurrentSlideIndex(newIndex);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('update-slides', {
        body: {
          deckId,
          slide_deck_json: deck
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Slides saved successfully"
      });
      
      onSave();
    } catch (error) {
      console.error('Save error:', error);
      toast({
        variant: "destructive",
        title: "Error saving",
        description: error instanceof Error ? error.message : "Failed to save"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-slides-pptx', {
        body: { deckId }
      });

      if (error) throw error;

      if (data?.filePath) {
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('slide-decks')
          .download(data.filePath);

        if (downloadError) throw downloadError;

        const url = URL.createObjectURL(fileData);
        const a = document.createElement('a');
        a.href = url;
        a.download = `slides-${deckId}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Slides exported and downloaded"
        });
        await markFreeCourseDownloadUsed();
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: "Error exporting",
        description: error instanceof Error ? error.message : "Export failed"
      });
    } finally {
      setExporting(false);
    }
  };

  const handleApplyTheme = (theme: SlideTheme) => {
    setDeck(prev => ({
      ...prev,
      theme
    }));
    
    toast({
      title: "Theme applied",
      description: "The visual theme has been updated. Save changes to persist."
    });
  };

  const handleGenerateImages = async () => {
    setGeneratingImages(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-slide-images', {
        body: {
          slides: deck.slides.map(s => ({
            id: s.id,
            title: s.title,
            bullets: s.bullets
          }))
        }
      });

      if (error) throw error;

      if (data?.slides) {
        setDeck(prev => ({
          ...prev,
          slides: prev.slides.map(slide => {
            const updatedSlide = data.slides.find((s: any) => s.id === slide.id);
            return updatedSlide ? { ...slide, imageUrl: updatedSlide.imageUrl } : slide;
          })
        }));

        toast({
          title: "Images generated!",
          description: "AI-generated images have been added to your slides. Save to persist."
        });
      }
    } catch (error) {
      console.error('Image generation error:', error);
      toast({
        variant: "destructive",
        title: "Error generating images",
        description: error instanceof Error ? error.message : "Failed to generate images"
      });
    } finally {
      setGeneratingImages(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{deck.title}</h1>
              <p className="text-sm text-muted-foreground">
                {deck.slides.length} slides
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleGenerateImages} disabled={generatingImages} variant="outline">
              <Sparkles className="mr-2 h-4 w-4" />
              {generatingImages ? 'Generating...' : 'AI Images'}
            </Button>
            <Button onClick={() => setThemeDialogOpen(true)} variant="outline">
              <Palette className="mr-2 h-4 w-4" />
              AI Theme
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button onClick={handleExport} disabled={exporting} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Slide List */}
          <div className="w-64 border-r bg-muted/30">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-2">
                <div className="text-xs font-semibold text-muted-foreground mb-3 px-1">
                  SLIDES ({deck.slides.length})
                </div>
                {deck.slides.map((slide, idx) => (
                  <Card
                    key={`slide-${slide.id}-${idx}`}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      idx === currentSlideIndex 
                        ? 'border-primary border-2 bg-primary/5' 
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => {
                      console.log('Navigating to slide:', idx);
                      setCurrentSlideIndex(idx);
                    }}
                  >
                    <CardHeader className="p-3">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="truncate">
                          {idx + 1}. {slide.title || 'Untitled Slide'}
                        </span>
                        {idx === currentSlideIndex && (
                          <span className="ml-2 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                ))}
                <Button onClick={addSlide} variant="outline" className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Slide
                </Button>
              </div>
            </ScrollArea>
          </div>

          {/* Center - Slide Editor */}
          <div className="flex-1 overflow-auto">
            <div className="max-w-6xl mx-auto p-8">
              <Tabs defaultValue="visual" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="visual">
                    <Layout className="mr-2 h-4 w-4" />
                    Visual Editor
                  </TabsTrigger>
                  <TabsTrigger value="text">
                    <Code className="mr-2 h-4 w-4" />
                    Text Editor
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="visual" className="space-y-4">
                  {/* Navigation Controls */}
                  <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                    <Button
                      onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                      disabled={currentSlideIndex === 0}
                      size="sm"
                      variant="outline"
                    >
                      ← Previous
                    </Button>
                    <span className="text-sm font-medium">
                      Slide {currentSlideIndex + 1} of {deck.slides.length}
                    </span>
                    <Button
                      onClick={() => setCurrentSlideIndex(Math.min(deck.slides.length - 1, currentSlideIndex + 1))}
                      disabled={currentSlideIndex === deck.slides.length - 1}
                      size="sm"
                      variant="outline"
                    >
                      Next →
                    </Button>
                  </div>

                  <VisualSlideCanvas 
                    key={`canvas-${currentSlide.id}-${currentSlideIndex}-${JSON.stringify(deck.theme)}`}
                    slide={currentSlide}
                    theme={deck.theme}
                    onUpdate={updateSlide}
                  />
                  
                  {/* Speaker Notes */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Speaker Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={currentSlide.notes}
                        onChange={(e) => updateSlide({ notes: e.target.value })}
                        placeholder="Add speaker notes..."
                        rows={4}
                      />
                    </CardContent>
                  </Card>

                  {/* Slide Controls */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Slide Controls</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => moveSlide('up')}
                          disabled={currentSlideIndex === 0}
                          variant="outline"
                          size="sm"
                        >
                          <ArrowUp className="mr-2 h-4 w-4" />
                          Move Up
                        </Button>
                        <Button
                          onClick={() => moveSlide('down')}
                          disabled={currentSlideIndex === deck.slides.length - 1}
                          variant="outline"
                          size="sm"
                        >
                          <ArrowDown className="mr-2 h-4 w-4" />
                          Move Down
                        </Button>
                        <Button
                          onClick={deleteSlide}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Slide
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="text">
                  <Card>
                    <CardHeader>
                      <div className="space-y-4">
                        <Input
                          value={currentSlide.title}
                          onChange={(e) => updateSlide({ title: e.target.value })}
                          placeholder="Slide title"
                          className="text-2xl font-bold"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Bullets */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Bullet Points</label>
                        {currentSlide.bullets.map((bullet, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              value={bullet}
                              onChange={(e) => updateBullet(idx, e.target.value)}
                              placeholder={`Bullet point ${idx + 1}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeBullet(idx)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button onClick={addBullet} variant="outline" size="sm">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Bullet
                        </Button>
                      </div>

                      {/* Notes */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Speaker Notes</label>
                        <Textarea
                          value={currentSlide.notes}
                          onChange={(e) => updateSlide({ notes: e.target.value })}
                          placeholder="Add speaker notes..."
                          rows={4}
                        />
                      </div>

                      {/* Slide Controls */}
                      <div className="flex gap-2 pt-4 border-t">
                        <Button
                          onClick={() => moveSlide('up')}
                          disabled={currentSlideIndex === 0}
                          variant="outline"
                          size="sm"
                        >
                          <ArrowUp className="mr-2 h-4 w-4" />
                          Move Up
                        </Button>
                        <Button
                          onClick={() => moveSlide('down')}
                          disabled={currentSlideIndex === deck.slides.length - 1}
                          variant="outline"
                          size="sm"
                        >
                          <ArrowDown className="mr-2 h-4 w-4" />
                          Move Down
                        </Button>
                        <Button
                          onClick={deleteSlide}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Slide
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
      
      <ThemeDesignerDialog
        open={themeDialogOpen}
        onOpenChange={setThemeDialogOpen}
        currentTheme={deck.theme}
        onApplyTheme={handleApplyTheme}
      />
    </div>
  );
};

export default SlideEditor;
